import { Grid2, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Divider, IconButton, Box, CircularProgress, Button } from "@mui/material";
import { Fragment, useRef } from "react";
import { PictureAsPdf, Delete, AttachFile, FileUpload } from "@mui/icons-material";
import { UploadedDocument } from "@/types/document";

export interface SettingsFileListProps {
  files: UploadedDocument[];
  onDelete: (filename: string) => void;
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
  uploadingInProgress?: boolean;
  tokensPerPage?: number;
  label?: string;
  error?: string;
}

export const SettingsFileList = ({ 
  files, 
  onDelete, 
  onUpload, 
  loading, 
  uploadingInProgress, 
  tokensPerPage = 1000,
  label,
  error 
}: SettingsFileListProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Calculate total tokens from all documents
  const totalTokens = files.reduce((sum, doc) => sum + (doc.tokenCount || 0), 0);
  // Convert tokens to pages
  const estimatedPages = Math.ceil(totalTokens / tokensPerPage);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  return (
    <Grid2 container spacing={2} sx={{ width: '100%' }}>
      <Grid2 sx={{ xs: 12, width: '100%' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ width: '100%' }}>
          {label && (
            <Typography variant="subtitle1">
              {label}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleUploadClick}
            startIcon={<FileUpload />}
            disabled={uploadingInProgress}
            size="small"
          >
            {uploadingInProgress ? "Wird hochgeladen..." : "Dokumente hochladen"}
            {uploadingInProgress && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Button>
        </Box>
        
        {error && (
          <Typography variant="body2" color="error" mb={1}>
            {error}
          </Typography>
        )}
        
        <Box mb={2} sx={{ width: '100%' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple
            onChange={onUpload}
            style={{ display: 'none' }}
            disabled={uploadingInProgress}
          />
          
          {/* Files list */}
          {loading ? (
            <CircularProgress size={24} />
          ) : files.length === 0 ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              py={3} 
              onClick={uploadingInProgress ? undefined : handleUploadClick}
              sx={{ border: '1px dashed #ccc', borderRadius: '4px', width: '100%' }}
            >
              <Typography variant="body2" color="textSecondary" align="center">
                Keine Dateien vorhanden. Laden Sie Dokumente hoch, um ein Profil zu generieren.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Token count summary */}
              {files.some(doc => doc.tokenCount !== undefined) && (
                <Box mb={2} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                  <Typography variant="body2" color="textSecondary">
                    Geschätzter Umfang: <strong>{estimatedPages} {estimatedPages === 1 ? 'Seite' : 'Seiten'} </strong> 
                    in {files.filter(doc => doc.tokenCount !== undefined).length} Dokumenten
                  </Typography>
                </Box>
              )}
              
              <Paper variant="outlined" sx={{ maxHeight: '300px', overflow: 'auto', width: '100%' }}>
                <List dense>
                  {files.map((file, index) => (
                    <Fragment key={file.filename}>
                      <ListItem
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => onDelete(file.filename)}
                            disabled={uploadingInProgress}
                            size="small"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        }
                        sx={{ py: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PictureAsPdf fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.displayName}
                          secondary={
                            <>
                              {new Date(file.uploadedAt).toLocaleString()}
                              {file.tokenCount !== undefined && (
                                <span style={{ marginLeft: 8 }}>
                                  • {Math.ceil(file.tokenCount / tokensPerPage).toLocaleString()} {Math.ceil(file.tokenCount / tokensPerPage) === 1 ? 'Seite' : 'Seiten'}
                                </span>
                              )}
                            </>
                          }
                          primaryTypographyProps={{ fontSize: '0.9rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItem>
                      {index < files.length - 1 && <Divider component="li" />}
                    </Fragment>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </Box>
      </Grid2>
    </Grid2>
  );
}; 